import AsyncSelect from 'react-select/async';
import { NoOptionsMessage } from '@/components/select';
import { Text } from '@chakra-ui/react';
import { KSelectStylesConfig } from '@/ui-library/design-system/constants/KSelect.styles';
import { CustomOptions } from './CustomOptions';
import { getCustomStyles } from '@/theme/components/select';
import {
  useAsyncSelect,
  OptionType,
  FetchConfig,
} from './hooks/useAsyncSelect';

type StyleVersionType = 'v1' | 'v2' | 'v3';

interface ItemProps {
  name?: string;
  options?: OptionType[];
  isMulti?: boolean;
  customFormatLabel?: (
    _option: OptionType,
    _labelKey: string,
    _item: ItemProps
  ) => React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  components?: Record<string, any>;
  validationType?: string;
  menuPlacement?: 'auto' | 'top' | 'bottom';
  valueKey?: string;
  labelKey?: string;
  fetchConfig?: FetchConfig;
  refreshOptions?: boolean;
  pluckById?: boolean;
  initialSearchQuery?: string;
  useInput?: boolean;
  disabledMessage?: string;
  customNoOptionsMessage?: (_props: any) => React.ReactNode;
  customOnChange?: (_selectedOption: any) => void;
  loadDefaultOptions?: boolean;
  defaultOptions?: OptionType[];
  customStyles?: Record<string, any>;
  hint?: any;
  styleVersion?: StyleVersionType;
  updateEndpointOnChange?: boolean;
  initialSelectedOption?: OptionType | null;
  showAvatar?: boolean;
}

interface AutoCompleteProps {
  item?: ItemProps;
  formik?: { submitCount?: number };
}

export const KAutoComplete = ({
  item = {},
  formik = {},
}: AutoCompleteProps) => {
  const {
    name = '',
    options: staticOptions = [],
    isMulti = false,
    customFormatLabel,
    placeholder = 'Select..',
    disabled = false,
    components = {},
    validationType,
    menuPlacement = 'auto',
    valueKey = 'value',
    labelKey = 'label',
    pluckById = false,
    fetchConfig = {},
    refreshOptions,
    initialSearchQuery = '',
    useInput = false,
    disabledMessage,
    customNoOptionsMessage,
    customOnChange = () => {},
    loadDefaultOptions = true,
    defaultOptions = [],
    customStyles = {},
    hint = () => {},
    styleVersion = 'v2',
    initialSelectedOption = null,
    showAvatar = false,
  } = item;
  const { submitCount = 0 } = formik;

  // Use custom hook for select logic
  const {
    meta,
    getValues,
    selectedOptions,
    uniqueDefaultOptionsWithInput,
    onChange,
    debouncedLoadOptions,
  } = useAsyncSelect({
    name,
    staticOptions,
    isMulti,
    valueKey,
    labelKey,
    fetchConfig,
    pluckById,
    useInput,
    initialSearchQuery,
    loadDefaultOptions,
    defaultOptions,
    refreshOptions,
    customOnChange,
    initialSelectedOption,
  });

  const styles = KSelectStylesConfig({ variant: 'default', customStyles });

  const styleVersionMap = {
    v1: getCustomStyles(),
    v2: styles,
  };

  const selectStyles = styleVersionMap[styleVersion] || styles;
  const showError = (submitCount > 0 || meta.touched) && meta.error;
  const values = getValues(validationType);

  // For single select, extract the first item from the array
  const selectedValue = isMulti ? selectedOptions : selectedOptions[0] || null;
  // Format option label
  const formatOptionLabel = (option: OptionType) =>
    customFormatLabel
      ? customFormatLabel(option, labelKey, item)
      : option?.[labelKey];

  // Custom no options message
  const NoOptionsMessageComp = (props: any) =>
    customNoOptionsMessage
      ? customNoOptionsMessage(props)
      : NoOptionsMessage(props);

  const asyncDefaultOptions = loadDefaultOptions
    ? useInput
      ? uniqueDefaultOptionsWithInput
      : true
    : false;

  return (
    <>
      {hint && hint()}

      <AsyncSelect
        isMulti={isMulti}
        defaultOptions={asyncDefaultOptions}
        value={values || selectedValue || initialSelectedOption}
        loadOptions={debouncedLoadOptions}
        inputId={name}
        formatOptionLabel={formatOptionLabel}
        className="kula_custom_select__container"
        classNamePrefix="kula_custom_select"
        hasError={showError}
        menuPlacement={menuPlacement}
        isClearable={false}
        isDisabled={disabled}
        styles={selectStyles}
        placeholder={placeholder}
        getOptionLabel={(option) => option?.[labelKey]}
        getOptionValue={(option) => option?.[valueKey]}
        onChange={onChange}
        hasIcon={showAvatar}
        {...item}
        components={{
          IndicatorSeparator: null,
          NoOptionsMessage: NoOptionsMessageComp,
          Option: CustomOptions,
          ...components,
        }}
      />
      {disabled && disabledMessage && (
        <Text mt="5px" color="red">
          {disabledMessage}
        </Text>
      )}
    </>
  );
};
